import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import styled from 'styled-components';
import { adsEnabled } from '../../utils/adsEnabled';

const Wrap = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radiusSm};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const LinkEl = styled.a`
  display: block;
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 12rem;
  object-fit: cover;
  display: block;
  transition: opacity 0.3s;
  @media (min-width: 768px) {
    height: 16rem;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  pointer-events: none;
`;

const Bottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.25rem 1.5rem;
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const SponsorLine = styled.span`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: #fff;
  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontClamp.xl};
  }
`;

const Desc = styled.p`
  margin: 0.25rem 0 0 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.85);
  max-width: 36rem;
`;

const CtaBtn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  z-index: 2;
  &:hover {
    background: rgba(15, 23, 42, 0.65);
  }
  ${({ $left }) => ($left ? 'left: 0.75rem;' : 'right: 0.75rem;')}
`;

const Dots = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  z-index: 2;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '1rem' : '0.5rem')};
  height: 0.5rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  padding: 0;
  background: ${({ $active }) => ($active ? 'hsl(43, 96%, 56%)' : 'rgba(255,255,255,0.45)')};
  transition: width 0.2s, background 0.2s;
`;

function AdCarousel({ ads, autoPlay = true, interval = 5000 }) {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((p) => (p + 1) % ads.length);
  }, [ads.length]);

  const prev = useCallback(() => {
    setCurrent((p) => (p - 1 + ads.length) % ads.length);
  }, [ads.length]);

  useEffect(() => {
    if (!autoPlay || ads.length < 2) return undefined;
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [autoPlay, interval, next, ads.length]);

  if (!adsEnabled() || !ads.length) return null;

  const ad = ads[current];

  return (
    <Wrap>
      <LinkEl href={ad.url} target="_blank" rel="noopener noreferrer sponsored">
        <Img src={ad.image} alt={ad.title} loading="lazy" key={ad.id} />
        <Overlay />
        <Bottom>
          <SponsorLine>
            Sponsored · {ad.sponsor}
          </SponsorLine>
          <Title>{ad.title}</Title>
          <Desc>{ad.description}</Desc>
          <CtaBtn>
            {ad.cta} <ExternalLink size={12} />
          </CtaBtn>
        </Bottom>
      </LinkEl>
      {ads.length > 1 && (
        <>
          <NavBtn type="button" $left aria-label="Previous ad" onClick={(e) => { e.preventDefault(); prev(); }}>
            <ChevronLeft size={16} />
          </NavBtn>
          <NavBtn type="button" aria-label="Next ad" onClick={(e) => { e.preventDefault(); next(); }}>
            <ChevronRight size={16} />
          </NavBtn>
          <Dots>
            {ads.map((_, i) => (
              <Dot
                key={i}
                type="button"
                $active={i === current}
                aria-label={`Go to ad ${i + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrent(i);
                }}
              />
            ))}
          </Dots>
        </>
      )}
    </Wrap>
  );
}

export default AdCarousel;
